import type {
  DataSchema,
  Integration,
  IntegrationAuthOption,
} from '@membranehq/sdk';

export enum IntegrationAuthType {
  OAUTH1 = 'oauth1',
  OAUTH2 = 'oauth2',
  PROXY = 'proxy',
  AUTH_PROXY_KEY = 'auth-proxy',
  OAUTH_VIA_INTEGRATION_LABEL = 'OAuth (via Integration.app)',
}

export enum OAuthStatus {
  Idle = 'idle',
  Waiting = 'waiting',
  Closed = 'closed',
  Success = 'success',
}

export const isOAuthOrProxyOption = (
  option: IntegrationAuthOption,
): boolean => {
  const optionType = option.type;
  return (
    optionType === IntegrationAuthType.OAUTH2 ||
    optionType === IntegrationAuthType.OAUTH1 ||
    optionType === IntegrationAuthType.PROXY ||
    option.key === IntegrationAuthType.AUTH_PROXY_KEY
  );
};

export type SelectableAuthOption = {
  key: string;
  label: string;
  isOAuth: boolean;
};

export const getSelectableAuthOptions = (
  integration: Integration | null,
): SelectableAuthOption[] => {
  const authOptions = integration?.authOptions;
  if (!authOptions?.length) return [];

  return authOptions
    .filter((opt) => opt.key)
    .map((opt) => ({
      key: opt.key!,
      label: opt.title || opt.key!,
      isOAuth: isOAuthOrProxyOption(opt),
    }));
};

export const getFieldsFromAuthOptions = (
  integrationData: Integration | null,
): string[] => {
  const authOptions = integrationData?.authOptions;

  if (!authOptions || authOptions.length === 0) {
    return [];
  }

  const fields: string[] = [];

  authOptions.forEach((option: IntegrationAuthOption) => {
    if (isOAuthOrProxyOption(option)) {
      return;
    }

    const schema = option.ui?.schema;
    if (schema?.properties) {
      const schemaFields = Object.keys(schema.properties);
      const filteredFields = schemaFields.filter((field) => {
        return (
          field !== IntegrationAuthType.AUTH_PROXY_KEY &&
          field !== IntegrationAuthType.OAUTH_VIA_INTEGRATION_LABEL
        );
      });
      fields.push(...filteredFields);
    } else if (
      option.key &&
      option.key !== IntegrationAuthType.AUTH_PROXY_KEY
    ) {
      fields.push(option.key);
    }
  });

  return fields;
};

export const getSchemaForIntegration = (
  integrationData: Integration | null,
): DataSchema | null => {
  if (!integrationData) return null;

  // Try multiple possible schema locations in the Integration type
  // The spec property contains the connector specification which may have UI schema
  return integrationData.spec?.ui?.schema || null;
};

export const isOAuth = (integration: Integration | null): boolean => {
  if (!integration) return false;

  const authType = integration.authType;
  if (
    authType === IntegrationAuthType.OAUTH2 ||
    authType === IntegrationAuthType.OAUTH1
  ) {
    return true;
  }

  const authOptions = integration.authOptions;
  if (authOptions && authOptions.length > 0) {
    const hasOAuthOption = authOptions.some(isOAuthOrProxyOption);
    if (hasOAuthOption) {
      return true;
    }

    const schema = getSchemaForIntegration(integration);
    const schemaFields = schema?.properties
      ? Object.keys(schema.properties)
      : [];
    const authFields = getFieldsFromAuthOptions(integration);
    const allFields = [...authFields, ...schemaFields];

    const hasOnlyOAuthFields =
      allFields.length > 0 &&
      allFields.every(
        (field) =>
          field === IntegrationAuthType.AUTH_PROXY_KEY ||
          field === IntegrationAuthType.OAUTH_VIA_INTEGRATION_LABEL,
      );

    if (hasOnlyOAuthFields) {
      return true;
    }
  }

  return false;
};

export const hasRealInputFields = (
  integration: Integration | null,
): boolean => {
  if (!integration) return false;

  const schema = getSchemaForIntegration(integration);
  const schemaFields = schema?.properties ? Object.keys(schema.properties) : [];
  const authFields = getFieldsFromAuthOptions(integration);

  const realFields = [...authFields, ...schemaFields].filter((field) => {
    return (
      field !== IntegrationAuthType.AUTH_PROXY_KEY &&
      field !== IntegrationAuthType.OAUTH_VIA_INTEGRATION_LABEL
    );
  });

  return realFields.length > 0;
};

export const getOAuthOptionKey = (
  integration: Integration | null,
): string | undefined => {
  if (!integration) return undefined;

  const authOptions = integration.authOptions;
  if (!authOptions) return undefined;

  const oauthOption = authOptions.find(isOAuthOrProxyOption);

  return oauthOption?.key;
};

const filterMarkerFields = (fields: string[]): string[] =>
  fields.filter(
    (field) =>
      field !== IntegrationAuthType.AUTH_PROXY_KEY &&
      field !== IntegrationAuthType.OAUTH_VIA_INTEGRATION_LABEL,
  );

export const getFilteredFields = (
  integration: Integration | null,
): string[] => {
  if (!integration) return [];

  const schema = getSchemaForIntegration(integration);
  const schemaFields = schema?.properties ? Object.keys(schema.properties) : [];
  const authFields = getFieldsFromAuthOptions(integration);

  const allFields = Array.from(new Set([...authFields, ...schemaFields]));
  return filterMarkerFields(allFields);
};

export const getFilteredFieldsForOption = (
  integration: Integration | null,
  authOptionKey?: string,
): string[] => {
  if (!integration) return [];

  const schema = getSchemaForIntegration(integration);
  const schemaFields = schema?.properties ? Object.keys(schema.properties) : [];

  if (!authOptionKey) {
    return getFilteredFields(integration);
  }

  const authOptions = integration.authOptions;
  const option = authOptions?.find((opt) => opt.key === authOptionKey);
  let optionFields: string[] = [];
  if (option && !isOAuthOrProxyOption(option)) {
    const optionSchema = option.ui?.schema;
    if (optionSchema?.properties) {
      optionFields = Object.keys(optionSchema.properties);
    } else if (option.key) {
      optionFields = [option.key];
    }
  }

  const allFields = Array.from(new Set([...schemaFields, ...optionFields]));
  return filterMarkerFields(allFields);
};

export const hasRealInputFieldsForOption = (
  integration: Integration | null,
  authOptionKey?: string,
): boolean => {
  return getFilteredFieldsForOption(integration, authOptionKey).length > 0;
};
